import React, { useMemo } from 'react';
import clsx from 'clsx';
import styles from './index.module.scss';
import ApproveRetakeButtons, {
  ApproveRetakeButtonsProps,
} from 'features/ApproveRetakeButtons';
import { ReactComponent as CheckIcon } from 'assets/images/check.svg';
import ImagePreviewMessage from './ImagePreviewMessage';
import MessageBanner from 'components/MessageBanner';
import ManualUploadButton from 'features/Driver/ManualUploadButton';
import BordersProgressBar from 'components/BordersProgressBar';
import FileSelection from 'components/FileSelection';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';

export enum ImagePreviewStatus {
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error',
}

export interface ImagePreviewProps {
  className?: string;
  source?: string;
  buttons?: ApproveRetakeButtonsProps;
  status?: ImagePreviewStatus;
  imageUploadProgress?: number;
  errorMessage?: string;
  errorLabel?: string;
  documentRequestId?: string;
  documentId?: string;
  documentType?: string;
  documentTypeEnum?: DocumentTypeEnum;
  hideManualUploadButton?: boolean;
  onManualUploadButtonClick?: () => void;
  file?: File;
}

const statusForProgressBar = [
  ImagePreviewStatus.UPLOADING,
  ImagePreviewStatus.PROCESSING,
];

function ImagePreview(props: ImagePreviewProps) {
  const isPDF = useMemo(() => {
    return (
      props.source?.startsWith('data:application/pdf;') ||
      props.file?.type === 'application/pdf'
    );
  }, [props.source]);
  if (!props.source) return null;

  return (
    <div className={clsx(props.className)}>
      <div className={styles.imgContainer}>
        {props.status === ImagePreviewStatus.UPLOADING &&
        props.imageUploadProgress ? (
          <BordersProgressBar
            progress={props.imageUploadProgress}
            key="withProgress"
          />
        ) : null}
        {props.status === ImagePreviewStatus.PROCESSING ? (
          <BordersProgressBar isAnimation key="withAnimation" />
        ) : null}

        {!isPDF && (
          <img
            src={props.source}
            width={'100%'}
            className={clsx(styles.image, {
              [styles.imageUploading]: statusForProgressBar.includes(
                props.status!
              ),
              [styles.imageInfo]:
                props.status === ImagePreviewStatus.PROCESSING,
              [styles.imageError]: props.status === ImagePreviewStatus.ERROR,
              [styles.imageSuccess]:
                props.status === ImagePreviewStatus.SUCCESS,
            })}
          />
        )}

        {isPDF && <FileSelection file={props.file!} />}

        {props.status === ImagePreviewStatus.UPLOADING && (
          <ImagePreviewMessage type="info" center>
            Uploading...
          </ImagePreviewMessage>
        )}
        {props.status === ImagePreviewStatus.PROCESSING && (
          <ImagePreviewMessage type="info" center>
            Processing...
          </ImagePreviewMessage>
        )}
        {props.status === ImagePreviewStatus.ERROR && (
          <ImagePreviewMessage type="error" center>
            {props.errorLabel || 'Image not valid'}
          </ImagePreviewMessage>
        )}
        {props.status === ImagePreviewStatus.SUCCESS && (
          <div className={clsx(styles.check)}>
            <CheckIcon />
          </div>
        )}
      </div>

      {props.status === ImagePreviewStatus.ERROR && props.errorMessage && (
        <MessageBanner className={styles.messageBanner}>
          {' '}
          {props.errorMessage}{' '}
        </MessageBanner>
      )}

      {props.status !== ImagePreviewStatus.PROCESSING &&
        props.status !== ImagePreviewStatus.UPLOADING && (
          <ApproveRetakeButtons
            {...props.buttons}
            className={styles.actionButtons}
          />
        )}

      {!props.hideManualUploadButton &&
        props.status === ImagePreviewStatus.ERROR &&
        props.documentRequestId &&
        props.documentId &&
        props.documentType && (
          <ManualUploadButton
            documentId={props.documentId}
            className={styles.manualUploadButton}
            documentRequestId={props.documentRequestId}
            documentType={props.documentType}
            documentTypeEnum={props.documentTypeEnum}
            onClick={props.onManualUploadButtonClick}
          />
        )}
    </div>
  );
}

export default ImagePreview;
